import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Usa variabile d'ambiente se presente, altrimenti URL fisso per Render
const API_URL = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : 'https://nova-network-2-0-backend.onrender.com/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem('token');
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [banInfo, setBanInfo] = useState(null);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        await fetchUser();
      } else {
        setLoading(false);
      }
    };
    initAuth();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      setBanInfo(null);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Pulisci il token solo se è un errore di autenticazione
      if (error.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { username, password });
      const { access_token, user: userData } = response.data;
      try {
        localStorage.setItem('token', access_token);
      } catch (e) {
        console.warn('localStorage not available');
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(userData);
      setBanInfo(null);
      return userData;
    } catch (error) {
      // Controlla se è un errore di ban
      if (error.response?.status === 403 && error.response?.data?.detail?.startsWith('BANNED|')) {
        setBanInfo(error.response.data.detail);
        throw new Error('BANNED');
      }
      throw error;
    }
  };

  const logout = () => {
    try {
      localStorage.removeItem('token');
    } catch (e) {
      console.warn('localStorage not available');
    }
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  // Nuove funzioni per il sistema di permessi
  const isSindacatoAdmin = () => user?.ruolo === 'SINDACATO_ADMIN';
  const isStaff = () => user?.ruolo === 'STAFF' || user?.ruolo === 'SINDACATO_ADMIN';
  const isCittadino = () => user?.ruolo === 'CITTADINO';
  const hasAzienda = () => !!user?.azienda_id || (user?.impieghi?.length > 0);
  
  // Verifica se l'utente è bannato
  const isBanned = () => !!user?.ban_info?.is_banned;
  
  // Verifica permesso specifico
  const hasPermesso = (permesso) => {
    if (isSindacatoAdmin()) return true;
    return user?.permessi?.includes(permesso) || false;
  };
  
  // Verifica se l'utente può operare in una specifica azienda
  const canOperateInAzienda = (aziendaId) => {
    if (isSindacatoAdmin()) return true;
    if (!aziendaId) return false;
    // Controlla se l'utente ha un impiego in questa azienda
    return user?.impieghi?.some(imp => imp.azienda_id === aziendaId) || user?.azienda_id === aziendaId;
  };
  
  // Ottieni tutte le aziende in cui l'utente può operare
  const getUserAziende = () => {
    if (isSindacatoAdmin()) return [];
    const aziende = user?.impieghi?.map(imp => ({
      id: imp.azienda_id,
      nome: imp.azienda_nome,
      ruolo: imp.ruolo_nome
    })) || [];
    // Aggiungi azienda principale se non presente negli impieghi
    if (user?.azienda_id && !aziende.some(a => a.id === user.azienda_id)) {
      aziende.push({ id: user.azienda_id, nome: user.azienda_nome, ruolo: user.ruolo_azienda_nome });
    }
    return aziende;
  };
  
  // Permessi specifici per comodità
  const canViewScontrini = () => hasPermesso('visualizza_scontrini');
  const canCreateScontrini = () => hasPermesso('crea_scontrini');
  const canManageDipendenti = () => hasPermesso('gestisci_dipendenti');
  const canManageConcorsi = () => hasPermesso('gestisci_concorsi');
  const canManageCurriculum = () => hasPermesso('gestisci_curriculum');
  const canManageLDL = () => hasPermesso('gestisci_ldl');
  const canPublishGazzetta = () => hasPermesso('pubblica_gazzetta');
  const canViewDocumenti = () => hasPermesso('visualizza_documenti') || user?.azienda_tipo === 'MUNICIPIO';
  const canManageDocumenti = () => hasPermesso('gestisci_documenti') || user?.azienda_tipo === 'MUNICIPIO';
  
  // Nuovi permessi - Scuola
  const canFirmaLezioni = () => hasPermesso('firma_lezioni');
  const canDirezioneScuola = () => hasPermesso('direzione_scuola');
  const canGestisciVoti = () => hasPermesso('gestisci_voti');
  
  // Nuovi permessi - Ospedale
  const canGestisciCartelleCliniche = () => hasPermesso('gestisci_cartelle_cliniche');
  const canVisualizzaCartelleCliniche = () => hasPermesso('visualizza_cartelle_cliniche') || hasPermesso('gestisci_cartelle_cliniche');
  
  // Nuovi permessi - Municipio
  const canGestisciSeggi = () => hasPermesso('gestisci_seggi');
  const canCreaDocumentiIdentita = () => hasPermesso('crea_documenti_identita');
  
  // Nuovi permessi - Banca
  const canGestisciConti = () => hasPermesso('gestisci_conti');
  const canVisualizzaConti = () => hasPermesso('visualizza_conti') || hasPermesso('gestisci_conti');
  
  // Nuovi permessi - Forze Ordine / Posta
  const canInserisciMulte = () => hasPermesso('inserisci_multe');
  const canGestisciPagamentiMulte = () => hasPermesso('gestisci_pagamenti_multe');
  const canVisualizzaFedina = () => hasPermesso('visualizza_fedina_penale') || hasPermesso('inserisci_multe');

  // Funzioni per verificare accesso ai moduli
  const canAccessScuola = () => canFirmaLezioni() || canDirezioneScuola() || canGestisciVoti();
  const canAccessOspedale = () => canGestisciCartelleCliniche() || canVisualizzaCartelleCliniche();
  const canAccessBanca = () => canGestisciConti() || canVisualizzaConti();
  const canAccessMulte = () => canInserisciMulte() || canGestisciPagamentiMulte() || canVisualizzaFedina();
  // Tutti i cittadini possono vedere e votare ai seggi aperti
  const canAccessSeggi = () => true;

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      logout,
      banInfo,
      // Ruoli
      isSindacatoAdmin,
      isStaff,
      isCittadino,
      hasAzienda,
      // Permessi generici
      hasPermesso,
      canOperateInAzienda,
      getUserAziende,
      canViewScontrini,
      canCreateScontrini,
      canManageDipendenti,
      canManageConcorsi,
      canManageCurriculum,
      canManageLDL,
      canPublishGazzetta,
      canViewDocumenti,
      canManageDocumenti,
      // Scuola
      canFirmaLezioni,
      canDirezioneScuola,
      canGestisciVoti,
      canAccessScuola,
      // Ospedale
      canGestisciCartelleCliniche,
      canVisualizzaCartelleCliniche,
      canAccessOspedale,
      // Municipio
      canGestisciSeggi,
      canCreaDocumentiIdentita,
      canAccessSeggi,
      // Banca
      canGestisciConti,
      canVisualizzaConti,
      canAccessBanca,
      // Forze Ordine / Posta
      canInserisciMulte,
      canGestisciPagamentiMulte,
      canVisualizzaFedina,
      canAccessMulte,
      // Ban
      isBanned
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
